/**
 * "File this ticket as a task" - the dialog behind the button on a ticket.
 *
 * A dialog rather than a one-click action, and a separate one from
 * `tasks/NewTaskModal`, for reasons that are worth stating because both look
 * like duplication until you read what they ask.
 *
 * WHY A DIALOG. The call pane files a next step with one click, because a step
 * is already one sentence somebody agreed to. A ticket is a conversation: its
 * subject is what the customer called the problem, which is rarely what the work
 * is, and the request itself is several paragraphs. So the operator gets the
 * derived values ON SCREEN and edits them before anything exists. Creating the
 * task silently and asking them to go and fix it afterwards is the step that
 * does not happen.
 *
 * WHY NOT `NewTaskModal`. That dialog creates a task from nothing and asks for a
 * client. This one starts from a record that already answers client, priority,
 * owner and often project, and asks the two questions that dialog has no field
 * for: what the work actually IS (a description, seeded with the customer's own
 * words) and whether it is chargeable. The shared half - the project and its
 * phase, with the rule that changing one clears the other - IS shared, as
 * `_shared/ProjectPhaseFields`.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not touch the ticket: no status
 * change, no reply, no note. Filing work is not answering the customer, and a
 * dialog that quietly moved the ticket to "in progress" would make the status
 * menu in the page head disagree with what the operator just did. The link
 * between the two is `Task.ticketId`, which both pages draw.
 *
 * The start date and the estimate are not asked either. They are refinements
 * with their own controls on the task page, and every field on a dialog is a
 * field somebody has to read before they can press the button.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { getProjects, type Project, type TaskPriority } from "../../../lib/projects";
import { createTicketTask, type Ticket, type TicketTask } from "../../../lib/support";
import { Btn, CheckField, MenuField, Modal } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ProjectPhaseFields } from "../_shared/ProjectPhaseFields";
import { UserPicker } from "../_shared/UserPicker";
import { PRIORITIES } from "../tasks/priority";
import { taskErrorKey } from "../tasks/taskErrors";
import { toIso } from "../projects/lib";

/** How many projects the picker offers. The register's own dialog is handed a
 *  page of rows by the list behind it; this dialog opens on a ticket and has to
 *  fetch its own, so it says out loud how many it asked for. */
const PROJECT_LIMIT = 200;

export function TicketTaskModal({
  ticket,
  /** Who is filing it. Seeds the owner when the ticket is on nobody. */
  actorId,
  onClose,
  onCreated,
}: {
  ticket: Ticket;
  actorId: string;
  onClose: () => void;
  onCreated: (task: TicketTask) => void;
}) {
  const t = useT();
  const { locale } = useI18n();

  // Seeded from the ticket, once. Re-seeding on every render would fight the
  // operator's typing, and the dialog is closed and thrown away after one use.
  const [title, setTitle] = useState(ticket.subject);
  const [description, setDescription] = useState(() => firstClientMessage(ticket));
  const [projectId, setProjectId] = useState("");
  const [milestoneId, setMilestoneId] = useState("");
  // The person the ticket is on, or the person filing it. The second half is the
  // difference between this and the server's own default: the API leaves an
  // unassigned ticket's task unowned, because it must not guess on the operator's
  // behalf. A dialog may suggest, because the suggestion is on screen and can be
  // taken out before the button is pressed - and a task described without an
  // owner is a task somebody has to come back and assign.
  const [assignees, setAssignees] = useState<string[]>(
    ticket.assignedTo ? [ticket.assignedTo] : actorId ? [actorId] : [],
  );
  // Carried, not reset: a ticket somebody marked urgent that becomes a normal
  // task is the ticket's urgency thrown away at the moment it was to be acted on.
  const [priority, setPriority] = useState<TaskPriority>(ticket.priority);
  const [due, setDue] = useState("");
  // Support work is often not chargeable, and this is the moment somebody knows.
  // The default still matches the column, so a task filed without a thought
  // behaves like every other task in the CRM.
  const [billable, setBillable] = useState(true);
  const [visibleToClient, setVisibleToClient] = useState(true);

  const [projects, setProjects] = useState<Project[]>([]);
  const [busy, setBusy] = useState(false);
  /** The catalogue key for the last refusal, not its text. One mapper for both
   *  task dialogs - see `tasks/taskErrors.ts` for why it is not two. */
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Fetched here rather than taken as a prop: neither screen that opens this
  // dialog (the ticket page, the queue) has a project list of its own, and
  // making them both fetch one they otherwise never use would put the request on
  // every ticket open instead of on the few that end in a task.
  useEffect(() => {
    let alive = true;
    getProjects({ pageSize: PROJECT_LIMIT, sort: "name", dir: "asc" })
      .then((page) => {
        if (!alive) return;
        setProjects(page.rows);
        // The ticket's own project, but only if it is one the picker can show.
        // `Ticket.projectId` is a loose string that may name a deleted or
        // archived project, and preselecting an id with no matching option
        // leaves a select drawn on "no project" while the state says otherwise -
        // the task would land somewhere the screen never showed.
        if (ticket.projectId && page.rows.some((p) => p.id === ticket.projectId)) {
          setProjectId(ticket.projectId);
        }
      })
      .catch(() => {
        // A task with no project is a valid task. Losing the picker is far
        // better than losing the dialog.
        if (alive) setProjects([]);
      });
    return () => {
      alive = false;
    };
  }, [ticket.projectId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const task = await createTicketTask(ticket.id, {
        title: title.trim(),
        // Sent even when empty, which is what makes clearing the seeded text
        // mean something: an absent key would let the server put the customer's
        // message back over a description the operator deliberately emptied.
        description: description.trim(),
        // Null, not undefined: absent means "inherit the ticket's project", and
        // an operator who chose "no project" is saying the opposite.
        projectId: projectId || null,
        milestoneId: milestoneId || null,
        assignees,
        priority,
        dueDate: toIso(due) ?? null,
        billable,
        visibleToClient,
      });
      onCreated(task);
      onClose();
    } catch (err) {
      // The dialog stays open and says why, beside the button that was pressed.
      setErrorKey(taskErrorKey(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("support.taskAddTitle")}
      label={t("support.taskAddTitle")}
      onClose={onClose}
      className="max-w-[460px]"
    >
      {/* `Modal` mounts no `PopHost`, and the project, phase and priority fields
          inside are `Pop` menus - without one they can be pressed and never
          open. Mounted here rather than by the caller: the ticket page has no
          host of its own. */}
      <PopHost>
        <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
          {/* What this task is, in the operator's words. The ticket's subject is
              what the CUSTOMER called the problem, which is a starting point and
              rarely the job. */}
          <div>
            <span className="crm-lab">{t("tasks.titleField")} *</span>
            <label className="crm-inp">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("tasks.titlePlaceholder")}
                autoComplete="off"
                autoFocus
                required
              />
            </label>
          </div>

          {/* Seeded with the customer's own first message. The person who picks
              this up on Thursday needs to read what was actually asked for, and
              they should not have to open the ticket to find out. */}
          <div>
            <span className="crm-lab">{t("support.taskDescription")}</span>
            {/* `crm-area`, the same control the new-ticket dialog next door uses -
                a textarea is not a `crm-inp` pill and wrapping one in a label
                would draw a pill round a box. */}
            <textarea
              className="crm-area"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("support.taskDescriptionPlaceholder")}
              rows={4}
            />
          </div>

          <ProjectPhaseFields
            projects={projects}
            projectId={projectId}
            milestoneId={milestoneId}
            onProjectChange={setProjectId}
            onMilestoneChange={setMilestoneId}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="crm-lab">{t("tasks.priority")}</span>
              <MenuField
                variant="form"
                value={priority}
                onChange={setPriority}
                label={t("tasks.priority")}
                options={PRIORITIES.map((p) => ({ value: p, label: t(`tasks.prio.${p}`) }))}
              />
            </div>
            <div>
              <span className="crm-lab">{t("tasks.dueDate")}</span>
              <DatePicker value={due} onChange={setDue} locale={locale} placeholder={t("tasks.dueDate")} />
            </div>
          </div>

          <div>
            <span className="crm-lab">{t("projects.assignees")}</span>
            <UserPicker multiple value={assignees} onChange={setAssignees} />
            {assignees.length === 0 ? (
              <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.unassigned")}</p>
            ) : null}
          </div>

          <CheckField checked={billable} onChange={setBillable} label={t("tasks.billable")} />

          {/* Last, and under a heading of its own - the same shape the task page
              uses, so the question looks the same wherever it is answered. It is
              not a second billing box: everything above this line is between us
              and our invoice, and this one is about what the customer opens. */}
          <div>
            <span className="crm-lab">{t("projects.clientVisibility")}</span>
            <CheckField
              checked={visibleToClient}
              onChange={setVisibleToClient}
              label={t("projects.visibleToClient")}
            />
          </div>

          {errorKey ? <p className="crm-alert text-[13px]">{t(errorKey)}</p> : null}

          <div className="crm-m-foot">
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="brand" disabled={busy || !title.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t("support.taskCreate")}
            </Btn>
          </div>
        </form>
      </PopHost>
    </Modal>
  );
}

/**
 * The customer's opening request, which is what the task is about.
 *
 * Internal notes are skipped, and that is not tidiness: `visibleToClient`
 * defaults to true and the client portal reads that column, so seeding the
 * description with a note the operator wrote for colleagues is one tick box away
 * from publishing it. The server applies the same filter when the dialog sends
 * nothing, so both paths agree.
 *
 * Falls back to an empty field rather than to the subject: the title already
 * says the subject, and repeating it as the body is a description that adds
 * nothing and looks like the form filled itself in wrongly.
 */
function firstClientMessage(ticket: Ticket): string {
  const first = (ticket.messages ?? []).find((m) => !m.internal);
  return first?.body.trim() ?? "";
}
